import { Signal, computed } from "@angular/core";
import { EmptyFeatureResult, SignalStoreFeature, signalStoreFeature, withComputed, withState } from "@ngrx/signals";

export type ErCallState = "init" | "loading" | "blocking-loading" | "loaded" | { error: string };

export type ErCallStateSlice = {
  callState: ErCallState;
};

export type ErNamedCallStateSlice<Collection extends string> = {
  [K in keyof ErCallStateSlice as Collection extends ""
    ? `${Collection}${K}`
    : `${Collection}${Capitalize<K>}`]: ErCallStateSlice[K];
};

export type ErCallStateSignals = {
  loading: Signal<boolean>;
  loaded: Signal<boolean>;
  error: Signal<string | null>;
};

export type ErNamedCallStateSignals<Prop extends string> = {
  [K in keyof ErCallStateSignals as Prop extends "" ? `${Prop}${K}` : `${Prop}${Capitalize<K>}`]: ErCallStateSignals[K];
};

export type SetCallState<Prop extends string | undefined> = Prop extends string
  ? ErNamedCallStateSlice<Prop>
  : ErCallStateSlice;

export function deriveCallStateKeys<Collection extends string>(collection?: Collection) {
  return {
    callStateKey: collection ? `${collection}CallState` : "callState",
    loadingKey: collection ? `${collection}Loading` : "loading",
    blockingLoadingKey: collection ? `${collection}BlockingLoading` : "blockingLoading",
    loadedKey: collection ? `${collection}Loaded` : "loaded",
    errorKey: collection ? `${collection}Error` : "error",
  };
}

export function getCallStateKeys(config?: { collection?: string }) {
  const prop = config?.collection;
  return deriveCallStateKeys(prop);
}

export function getCollectionArray(config: { collection?: string } | { collections?: string[] }) {
  return "collections" in config
    ? config.collections
    : "collection" in config && config.collection
      ? [config.collection]
      : undefined;
}

export function withErCallState<Collection extends string>(config: {
  collections: Collection[];
}): SignalStoreFeature<
  EmptyFeatureResult,
  EmptyFeatureResult & {
    state: ErNamedCallStateSlice<Collection>;
    props: ErNamedCallStateSignals<Collection>;
  }
>;
export function withErCallState<Collection extends string>(config: {
  collection: Collection;
}): SignalStoreFeature<
  EmptyFeatureResult,
  EmptyFeatureResult & {
    state: ErNamedCallStateSlice<Collection>;
    props: ErNamedCallStateSignals<Collection>;
  }
>;
export function withErCallState(): SignalStoreFeature<
  EmptyFeatureResult,
  EmptyFeatureResult & {
    state: ErCallStateSlice;
    props: ErCallStateSignals;
  }
>;
export function withErCallState<Collection extends string>(
  config?:
    | {
        collection: Collection;
      }
    | {
        collections: Collection[];
      },
): SignalStoreFeature {
  return signalStoreFeature(
    withState(() => {
      if (!config) {
        return { callState: "init" };
      }
      const collections = getCollectionArray(config);
      if (collections) {
        return collections.reduce(
          (acc, cur) => ({
            ...acc,
            ...{ [cur ? `${cur}CallState` : "callState"]: "init" },
          }),
          {},
        );
      }

      return { callState: "init" };
    }),
    withComputed((state: Record<string, Signal<unknown>>) => {
      if (config) {
        const collections = getCollectionArray(config);
        if (collections) {
          return collections.reduce<Record<string, Signal<unknown>>>((acc, cur: string) => {
            const { callStateKey, errorKey, loadedKey, blockingLoadingKey, loadingKey } = deriveCallStateKeys(cur);
            const callState = state[callStateKey] as Signal<ErCallState>;
            return {
              ...acc,
              [blockingLoadingKey]: computed(() => callState() === "blocking-loading"),
              [loadingKey]: computed(() => callState() === "loading"),
              [loadedKey]: computed(() => callState() === "loaded"),
              [errorKey]: computed(() => {
                const v = callState();
                return typeof v === "object" ? v.error : null;
              }),
            };
          }, {});
        }
      }
      const { callStateKey, errorKey, loadedKey, blockingLoadingKey, loadingKey } = deriveCallStateKeys();
      const callState = state[callStateKey] as Signal<ErCallState>;
      return {
        [blockingLoadingKey]: computed(() => callState() === "blocking-loading"),
        [loadingKey]: computed(() => callState() === "loading"),
        [loadedKey]: computed(() => callState() === "loaded"),
        [errorKey]: computed(() => {
          const v = callState();
          return typeof v === "object" ? v.error : null;
        }),
      };
    }),
  );
}

export function setLoading<Prop extends string | undefined = undefined>(prop?: Prop): SetCallState<Prop> {
  if (prop) {
    return { [`${prop}CallState`]: "loading" } as SetCallState<Prop>;
  }

  return { callState: "loading" } as SetCallState<Prop>;
}

export function setBlockingLoading<Prop extends string | undefined = undefined>(prop?: Prop): SetCallState<Prop> {
  if (prop) {
    return { [`${prop}CallState`]: "blocking-loading" } as SetCallState<Prop>;
  }

  return { callState: "blocking-loading" } as SetCallState<Prop>;
}

export function setLoaded<Prop extends string | undefined = undefined>(prop?: Prop): SetCallState<Prop> {
  if (prop) {
    return { [`${prop}CallState`]: "loaded" } as SetCallState<Prop>;
  } else {
    return { callState: "loaded" } as SetCallState<Prop>;
  }
}

export function setError<Prop extends string | undefined = undefined>(error: unknown, prop?: Prop): SetCallState<Prop> {
  let errorMessage: string;

  if (!error) {
    errorMessage = "";
  } else if (typeof error === "object" && "message" in error) {
    errorMessage = String(error.message);
  } else {
    errorMessage = String(error);
  }

  if (prop) {
    return {
      [`${prop}CallState`]: { error: errorMessage },
    } as SetCallState<Prop>;
  } else {
    return { callState: { error: errorMessage } } as SetCallState<Prop>;
  }
}
